import React, { useState, useEffect } from 'react';
import { X, Loader, TrendingUp, TrendingDown, RefreshCw, Calendar, Video, Info } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { getCreditHistory } from '../services/api';
import './CreditHistory.css';

const CreditHistory = ({ isOpen, onClose, userId }) => {
  const { language } = useLanguage();
  const t = translations[language];
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    if (isOpen && userId) {
      fetchCreditHistory(currentPage);
    }
  }, [isOpen, userId, currentPage]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const fetchCreditHistory = async (page) => {
    setLoading(true);
    try {
      const data = await getCreditHistory({
        page: page,
        limit: ITEMS_PER_PAGE
      });

      setTransactions(data.transactions || []);
      setTotalPages(data.totalPages || 1);
      setTotalTransactions(data.total || 0);
    } catch (error) {
      console.error('Error fetching credit history:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deduction':
        return <TrendingDown size={20} className="transaction-icon deduction" />;
      case 'reset':
      case 'purchase':
      case 'bonus':
        return <TrendingUp size={20} className="transaction-icon credit" />;
      default:
        return <RefreshCw size={20} className="transaction-icon neutral" />;
    }
  };

  const getTransactionTypeName = (type) => {
    const typeNames = {
      'deduction': t.creditHistory?.deduction || 'Video Generation',
      'reset': t.creditHistory?.reset || 'Daily Reset',
      'purchase': t.creditHistory?.purchase || 'Credit Purchase',
      'bonus': t.creditHistory?.bonus || 'Bonus Credits',
      'refund': t.creditHistory?.refund || 'Refund',
    };
    return typeNames[type] || type;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t.creditHistory?.justNow || 'Just now';
    if (diffMins < 60) return `${diffMins}${t.creditHistory?.minsAgo || 'm ago'}`;
    if (diffHours < 24) return `${diffHours}${t.creditHistory?.hoursAgo || 'h ago'}`;
    if (diffDays < 7) return `${diffDays}${t.creditHistory?.daysAgo || 'd ago'}`;
    
    return date.toLocaleDateString(language, { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(language, { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="credit-history-modal">
      <div className="credit-history-overlay" onClick={onClose} />
      
      <div className="credit-history-container">
        {/* Header */}
        <div className="credit-history-header">
          <div className="credit-history-title-section">
            <h2 className="credit-history-title">
              {t.creditHistory?.title || 'Credit History'}
            </h2>
            <p className="credit-history-subtitle">
              {totalTransactions} {t.creditHistory?.transactions || 'transactions'}
            </p>
          </div>
          <button className="credit-history-close" onClick={onClose} aria-label="Close">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="credit-history-content">
          {loading ? (
            <div className="credit-history-loading">
              <Loader className="spinner" size={40} />
              <p>{t.loading || 'Loading...'}</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="credit-history-empty">
              <div className="empty-icon">💳</div>
              <h3>{t.creditHistory?.noTransactions || 'No transactions yet'}</h3>
              <p>{t.creditHistory?.noTransactionsDesc || 'Your credit transactions will appear here'}</p>
            </div>
          ) : (
            <>
              {/* Transactions List */}
              <div className="credit-history-list">
                {transactions.map((transaction) => (
                  <div key={transaction.id} className="transaction-card">
                    <div className="transaction-main">
                      <div className="transaction-icon-wrapper">
                        {getTransactionIcon(transaction.transaction_type)}
                      </div>
                      
                      <div className="transaction-details">
                        <div className="transaction-header">
                          <h4 className="transaction-type">
                            {getTransactionTypeName(transaction.transaction_type)}
                          </h4>
                          <span className={`transaction-amount ${transaction.credits_amount >= 0 ? 'positive' : 'negative'}`}>
                            {transaction.credits_amount >= 0 ? '+' : ''}{transaction.credits_amount}
                          </span>
                        </div>
                        
                        <div className="transaction-meta">
                          <span className="transaction-date">
                            <Calendar size={14} />
                            {formatDate(transaction.created_at)}
                          </span>
                          <span className="transaction-time">
                            {formatTime(transaction.created_at)}
                          </span>
                        </div>

                        {transaction.description && (
                          <p className="transaction-description">
                            {transaction.description}
                          </p>
                        )}

                        {/* Video Info (for deductions) */}
                        {transaction.transaction_type === 'deduction' && transaction.video_scene_name && (
                          <div className="transaction-video-info">
                            <Video size={14} />
                            <span className="video-scene-name">
                              {transaction.video_scene_name}
                            </span>
                            {transaction.video_duration && (
                              <span className="video-duration">
                                • {formatDuration(transaction.video_duration)}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Balance Info */}
                        <div className="transaction-balance">
                          <span className="balance-item">
                            <Info size={12} />
                            {t.creditHistory?.balance || 'Balance'}: 
                            <strong> {transaction.credits_before} → {transaction.credits_after}</strong>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="credit-history-pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    {t.previous || 'Previous'}
                  </button>
                  
                  <div className="pagination-info">
                    <span className="pagination-page">
                      {t.creditHistory?.page || 'Page'} {currentPage} {t.of || 'of'} {totalPages}
                    </span>
                  </div>
                  
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    {t.next || 'Next'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditHistory;
